import axios from 'axios'
import apiConfig from '../globalConfig/apiConfig'

axios.defaults.headers.common['caller'] = 'EXTERNAL_UI'

axios.interceptors.response.use(undefined, (err) => {
  const { config } = err
  if (!config || !config.retry) {
    return Promise.reject(err)
  }
  config.retry -= 1
  const delayRetryRequest = new Promise((resolve) => {
    setTimeout(() => {
      resolve()
    })
  })
  return delayRetryRequest.then(() => axios(config))
})

// SPREADSHEET REQUESTS ------------------------------------------------------
export const downloadTemplate = (vendorId, costLocationGroupType) => {
  return axios.get(
    `${apiConfig.hesperidesexternal.url}/workbook/${vendorId}?costLocationGroupType=${costLocationGroupType}`,
    {
      responseType: 'arraybuffer',
      retry: 1,
    },
  )
}

export const getSpreadsheetHistory = (vendorId) => {
  return axios.get(
    `${apiConfig.hesperidesexternal.url}/workbook/vendor/${vendorId}/history`,
  )
}

export const uploadTemplate = (formData, vendorId) => {
  return axios.post(
    `${apiConfig.hesperidesexternal.url}/workbook/vendor/${vendorId}`,
    formData,
    {
      retry: 1,
    },
  )
}

// SAVED VIEW REQUESTS -------------------------------------------------------
export const getSavedViewsMetadata = (vendorId) => {
  return axios.get(
    `${apiConfig.hesperidesexternal.url}/view/metadata?vendorId=${vendorId}`,
    {
      retry: 1,
    },
  )
}

export const getSavedView = (viewId) => {
  return axios.get(`${apiConfig.hesperidesexternal.url}/view/${viewId}`, {
    retry: 1,
  })
}

export const saveView = (vendorId, savedViewForm) => {
  return axios.post(
    `${apiConfig.hesperidesexternal.url}/view?vendorId=${vendorId}`,
    savedViewForm,
    {
      retry: 1,
    },
  )
}

export const updateSaveView = (viewId, vendorId, savedViewForm) => {
  return axios.put(
    `${apiConfig.hesperidesexternal.url}/view/${viewId}?vendorId=${vendorId}`,
    savedViewForm,
    {
      retry: 1,
    },
  )
}

export const deleteSaveView = (viewId, vendorId) => {
  return axios.delete(
    `${apiConfig.hesperidesexternal.url}/view/${viewId}?vendorId=${vendorId}`,
    {
      retry: 1,
    },
  )
}

export const downloadSavedView = (viewId, vendorId) => {
  return axios.get(
    `${apiConfig.hesperidesexternal.url}/cost/view/${viewId}?vendorId=${vendorId}`,
    {
      responseType: 'arraybuffer',
      retry: 1,
    },
  )
}

// AUTOCOMPLETE OPTIONS REQUESTS ---------------------------------------------
export const getAutocompleteOptions = (fieldName, prefix = '', limit = 200) => {
  return axios.get(`${apiConfig.hesperidesexternal.url}/autocomplete`, {
    retry: 1,
    params: {
      'field-name': fieldName,
      prefix: prefix,
      limit: limit,
    },
  })
}

export const getClassOptions = (departmentId) => {
  return axios.get(
    `${apiConfig.hesperidesexternal.url}/autocomplete/classes/${departmentId}`,
    {
      retry: 1,
    },
  )
}

export const getDepartmentOptions = () => {
  return axios.get(
    `${apiConfig.hesperidesexternal.url}/autocomplete/departments`,
    {
      retry: 1,
    },
  )
}

export const getCustomLocationGroupOptions = (vendorId) => {
  return axios.get(
    `${apiConfig.hesperidesexternal.url}/autocomplete/customCostLocationGroups?vendorId=${vendorId}`,
    { retry: 1 },
  )
}

export const getSupplierLocationOptions = (vendorId) => {
  return axios.get(
    `${apiConfig.hesperidesexternal.url}/autocomplete/supplier-locations?vendorId=${vendorId}`,
    { retry: 1 },
  )
}

export const getCostOriginSourceOptions = () => {
  return axios.get(`${apiConfig.hesperidesexternal.url}/autocomplete/sources`, {
    retry: 1,
  })
}

export const getApprovalStatusOptions = () => {
  return axios.get(
    `${apiConfig.hesperidesexternal.url}/autocomplete/pendingCostChangeStatuses`,
    { retry: 1 },
  )
}

// VENDOR DETAILS REQUESTS ---------------------------------------------------
export const getAssociatedVendors = () => {
  return axios.get(`${apiConfig.hesperidesexternal.url}/vendors`, {
    retry: 1,
  })
}

// COST SEARCH REQUESTS ------------------------------------------------------
export const getLocationLevelCosts = (vendorId, body) => {
  return axios.post(
    `${apiConfig.hesperidesexternal.url}/cost/locations?vendorId=${vendorId}`,
    body,
    {
      retry: 1,
    },
  )
}

export const searchCosts = (vendorId, body) => {
  return axios.post(
    `${apiConfig.hesperidesexternal.url}/cost/search/${vendorId}`,
    body,
    {
      retry: 1,
    },
  )
}

export const downloadSearchedCosts = (vendorId, body) => {
  return axios.post(
    `${apiConfig.hesperidesexternal.url}/cost/search/${vendorId}`,
    body,
    {
      responseType: 'arraybuffer',
      headers: { Accept: 'application/octet-stream' },
      retry: 1,
    },
  )
}

export const submitCosts = (body) => {
  return axios.post(`${apiConfig.hesperidesexternal.url}/costchange`, body, {
    retry: 1,
  })
}

// COST INSIGHTS REQUESTS ----------------------------------------------------

export const getCostChangeItems = (requestBody) => {
  return axios.post(
    `${apiConfig.hesperidesexternal.url}/insight/items`,
    requestBody,
    {
      retry: 1,
    },
  )
}

export const getItemCostChangeHistory = (requestBody) => {
  return axios.post(
    `${apiConfig.hesperidesexternal.url}/insight/cost-changes`,
    requestBody,
    {
      retry: 1,
    },
  )
}

// CLG REQUESTS --------------------------------------------------------------

export const createCustomLocationGroup = (body) => {
  return axios.post(`${apiConfig.hesperidesexternal.url}/custom`, body, {
    retry: 1,
  })
}

export const deleteCustomLocationGroup = (vendorId, clgId) => {
  return axios.delete(
    `${apiConfig.hesperidesexternal.url}/custom/${clgId}?vendorId=${vendorId}`,
    {
      retry: 1,
    },
  )
}

export const getCustomLocationGroupById = (clgId) => {
  return axios.get(`${apiConfig.hesperidesexternal.url}/custom/${clgId}`, {
    retry: 1,
  })
}

export const updateCustomLocationGroup = (body, clgId) => {
  return axios.put(
    `${apiConfig.hesperidesexternal.url}/custom/${clgId}`,
    body,
    {
      retry: 1,
    },
  )
}

// NOTIFICATIONS REQUESTS ----------------------------------------------------
export const getNotifications = () => {
  return axios.get(`${apiConfig.hesperidesexternal.url}/rules`, {
    retry: 1,
  })
}

export const createNotification = (body) => {
  return axios.post(`${apiConfig.hesperidesexternal.url}/rule`, body, {
    retry: 1,
  })
}

export const updateNotification = (notificationId, body) => {
  return axios.put(
    `${apiConfig.hesperidesexternal.url}/rule/${notificationId}`,
    body,
    {
      retry: 1,
    },
  )
}

export const deleteNotification = (notificationId) => {
  return axios.delete(
    `${apiConfig.hesperidesexternal.url}/rule/${notificationId}`,
    {
      retry: 1,
    },
  )
}

export const deleteAllNotifications = () => {
  return axios.delete(`${apiConfig.hesperidesexternal.url}/rules`, {
    retry: 1,
  })
}

export const pauseNotification = (notificationId, startDate, endDate) => {
  const params = {}

  if (startDate) {
    params.startDate = startDate
  }

  if (endDate) {
    params.endDate = endDate
  }

  return axios.put(
    `${apiConfig.hesperidesexternal.url}/rule/${notificationId}/pause`,
    null,
    {
      params,
      retry: 1,
    },
  )
}

export const resumeNotification = (notificationId) => {
  return axios.put(
    `${apiConfig.hesperidesexternal.url}/rule/${notificationId}/resume`,
    null,
    {
      retry: 1,
    },
  )
}

export const pauseAllNotifications = (startDate, endDate) => {
  const params = {}

  if (startDate) {
    params.startDate = startDate
  }

  if (endDate) {
    params.endDate = endDate
  }

  return axios.put(`${apiConfig.hesperidesexternal.url}/rule/pause-all`, null, {
    params,
    retry: 1,
  })
}

export const resumeAllNotifications = () => {
  return axios.put(
    `${apiConfig.hesperidesexternal.url}/rule/resume-all`,
    null,
    {
      retry: 1,
    },
  )
}
